import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { delay, last } from "lodash";
import { Button, Flex, Popconfirm, Segmented, Space } from "antd";
import { SentenceType, StatusType, TaskType } from "App.types";
import Dictionary from "Components/Dictionary";
import StoryDialogSentence from "Components/StoryDialogSentence";
import { Element, scroller } from "react-scroll";
import cx from "classnames";
import TaskPanel from "Components/TaskPanel";
import { isMobile } from "react-device-detect";
import styles from "./DialogTask.module.scss";
import { SentenceTaskProps } from "../SentenceTask.type";
import DialogTaskOptions from "./DialogTaskOptions";
import DialogTaskTranslate from "./DialogTaskTranslate";
import DialogTaskRead from "./DialogTaskRead";
import { useLocation } from "react-router-dom";
import HintDrawer from "Components/HintDrawer";
import useTemplatedSentences from "../useTemplatedSentences";
import { AudioOutlined, EditOutlined, ReloadOutlined } from "@ant-design/icons";
import useSentenceAudio from "Hooks/useSentenceAudio";
import useSentencesAudios from "Hooks/useSentencesAudios";
import useSocketSpeech from "Hooks/useSocketSpeech";
import DialogTaskText from "./DialogTaskText";

const DialogTask: FC<SentenceTaskProps> = ({ autoPlay = true, lesson, onTaskComplete, onNext, sentence: taskSentence, showGrammar }) => {
  const [status, setStatus] = useState<StatusType>(StatusType.Empty);
  const [audioStatus, setAudioStatus] = useState<StatusType>(StatusType.Empty);
  const [activeSentIdx, setActiveSentIdx] = useState<number>(-1);
  const [isDictOpened, setDictOpened] = useState(false);
  const [hintDrawerStatus, setHintDrawerStatus] = useState<"closed" | "active" | "">();
  const [sentModes, setSentModes] = useState<boolean[]>([]);

  const location = useLocation();

  const isReview = useMemo(() => location.pathname.includes("/review"), [location]);

  const { sentences: defSentences = [], id: storyId } = taskSentence.linkedStory || {};

  const templatedSentences = useTemplatedSentences(defSentences);

  //@ts-ignore
  const sentences = useMemo<SentenceType[]>(() => {
    if (templatedSentences.every((s) => s.isLeft && !s.tasks?.[0])) {
      return [
        ...templatedSentences,
        { markers: [], phrases: [], tags: [], tasks: [TaskType.Text], text: "", translate: "", id: 0, isLeft: false },
      ];
    } else {
      return templatedSentences;
    }
  }, [templatedSentences]);

  const availableSentences = useMemo(
    () => (isReview ? sentences : sentences.slice(0, activeSentIdx + 1)),
    [sentences, activeSentIdx, isReview],
  );

  useEffect(() => {
    if (activeSentIdx >= 0 && activeSentIdx < sentences.length) {
      scroller.scrollTo(`${activeSentIdx}`, { smooth: true, isDynamic: true });
    }
  }, [activeSentIdx, sentences.length]);

  useEffect(() => {
    if (activeSentIdx >= 0)
      setSentModes((prev) => {
        const lastMode = last(prev) ?? true;
        return [...prev, lastMode];
      });
  }, [activeSentIdx]);

  useEffect(() => {
    if (status === StatusType.Empty && autoPlay && activeSentIdx === -1) setActiveSentIdx(0);
  }, [autoPlay, status, activeSentIdx]);

  const audios = useSentencesAudios({ sentences });

  const activeSent = sentences[activeSentIdx];

  const {
    start,
    stop,
    results: speechResults,
    isSpeaking,
  } = useSocketSpeech({
    text: [TaskType.Read, TaskType.VoiceTranslate, TaskType.Translate].includes(sentences[activeSentIdx]?.tasks[0])
      ? sentences[activeSentIdx]?.text
      : undefined,
    setStatus: setAudioStatus,
    interim_results: true,
  });

  useEffect(() => {
    if (![TaskType.Read, TaskType.VoiceTranslate, TaskType.Translate].includes(sentences[activeSentIdx]?.tasks[0])) {
      stop();
    }
  }, [activeSentIdx, sentences, stop]);

  const audio = useSentenceAudio({
    disabled: !activeSent?.text || (activeSent.tasks.length !== 0 && activeSent.tasks[0] !== TaskType.Listen),
    sentence: activeSent,
  });

  useEffect(() => {
    const fn = () => setActiveSentIdx((prev) => prev + 1);

    if (activeSent?.text && (activeSent.tasks.length === 0 || activeSent.tasks[0] === TaskType.Listen)) {
      audio.play().catch((err) => {
        console.log(err);
        delay(() => audio.play().catch(fn), 200);
      });
      audio.addEventListener("ended", fn);

      return () => audio.removeEventListener("ended", fn);
    }
  }, [audio, activeSent]);

  useEffect(() => {
    if (sentences.length && activeSentIdx >= sentences.length && status !== StatusType.Completed) {
      setStatus(StatusType.Completed);
      onTaskComplete();
    }
  }, [activeSentIdx, onTaskComplete, sentences.length, status]);

  const onRecordClick = useCallback(() => {
    if (audioStatus === StatusType.IsRecording) {
      stop();
    } else {
      start();
    }
  }, [audioStatus, start, stop]);

  useEffect(() => {
    if (availableSentences[activeSentIdx]?.hint) {
      setHintDrawerStatus("active");
    } else {
      setHintDrawerStatus(undefined);
    }
  }, [activeSentIdx, availableSentences]);

  const onSuccess = useCallback(() => {
    delay(() => setActiveSentIdx((prev) => prev + 1), 1000);
  }, []);

  return (
    <div className={cx(styles.storyTask, { [styles.storyTask_mobile]: isMobile })}>
      <div className={styles.storyTask__text}>
        <Flex vertical style={{ maxWidth: 680 }} gap={5}>
          {availableSentences.map((sentence, idx) => (
            <Element name={`${idx}`} key={sentence.id}>
              <Flex
                justify={sentence.isLeft ? "flex-start" : "flex-end"}
                style={{ marginTop: sentence.isLeft !== availableSentences[idx - 1]?.isLeft ? 5 : undefined }}
              >
                {![TaskType.MultiSelect, TaskType.VoiceTranslate, TaskType.Read, TaskType.Translate, TaskType.Text].includes(
                  sentence.tasks[0],
                ) ? (
                  <StoryDialogSentence
                    showTranslate={!sentence.text}
                    storyId={storyId}
                    hidden={activeSentIdx < idx && !isReview}
                    active={idx === activeSentIdx}
                    sentence={sentence}
                    audio={activeSentIdx === idx ? audio : audios[idx]}
                  />
                ) : sentence.tasks.includes(TaskType.Text) ? (
                  <DialogTaskText
                    storyId={storyId}
                    description={taskSentence.description}
                    lesson={lesson}
                    sentence={sentence}
                    onSuccess={onSuccess}
                  />
                ) : sentence.tasks.includes(TaskType.Read) ? (
                  <DialogTaskRead
                    speechResults={speechResults}
                    onClickRecording={onRecordClick}
                    audioStatus={audioStatus}
                    sentence={sentence}
                    isSpeaking={isSpeaking}
                    onSuccess={onSuccess}
                  />
                ) : [TaskType.VoiceTranslate, TaskType.Translate].includes(sentence.tasks[0]) ? (
                  <DialogTaskTranslate
                    isWriting={sentModes[idx]}
                    lesson={lesson}
                    speechResults={idx === activeSentIdx ? speechResults : undefined}
                    onClickRecording={onRecordClick}
                    audioStatus={audioStatus}
                    sentence={sentence}
                    isSpeaking={isSpeaking}
                    onSuccess={onSuccess}
                  />
                ) : (
                  <DialogTaskOptions audio={audios[idx]} storyId={taskSentence.linkedStoryId} sentence={sentence} onSuccess={onSuccess} />
                )}
              </Flex>
            </Element>
          ))}
        </Flex>
      </div>

      <TaskPanel
        sentId={taskSentence.id}
        lessonId={lesson.id}
        task={TaskType.Dialog}
        setDictOpened={setDictOpened}
        showGrammar={showGrammar}
        storyId={storyId}
        onNext={onNext}
        isCompleted={status === StatusType.Completed}
      >
        <Space>
          <Popconfirm
            title={"Начать заново?"}
            onConfirm={() => {
              setSentModes([]);
              setActiveSentIdx(-1);
            }}
          >
            <Button icon={<ReloadOutlined />} />
          </Popconfirm>

          {!activeSent?.tasks.includes(TaskType.Text) && (
            <Segmented
              size={"large"}
              value={sentModes[activeSentIdx]}
              onChange={(value) => {
                stop();
                setSentModes((prev) => {
                  prev[activeSentIdx] = value;
                  return [...prev];
                });
              }}
              options={[
                { icon: <AudioOutlined style={{ fontSize: 21 }} />, value: false },
                { value: true, icon: <EditOutlined style={{ fontSize: 21 }} /> },
              ]}
            />
          )}
        </Space>
      </TaskPanel>

      <Dictionary storyId={storyId} isOpen={isDictOpened} sentences={sentences} toggle={setDictOpened} />
      <div className={styles.placeholder} />
      <HintDrawer
        text={sentences[activeSentIdx]?.hint}
        open={hintDrawerStatus === "active"}
        onClose={() => setHintDrawerStatus("closed")}
      />
    </div>
  );
};

export default DialogTask;
